import $ from 'jquery';
import React from 'react';

import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AaSequenceSummary} from 'molbio/aa-sequences/util/AaSequenceConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FileSearchDropdown from 'ui-library/attach-dropdown/FileSearchDropdown';
import FormInput from 'ui-library/bs/FormInput';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

type AaSequenceWithPermissions = AaSequenceSummary & {permissions: Permissions};

interface Props {
  onChooseAaSequence: (protein: AaSequenceWithPermissions) => void;
}

interface State {
  query: string;
}

// Allows the user to search for and choose proteins from Benchling.
class AaSequenceSearchInput extends React.Component<Props, State> {
  override state: State = {
    query: '',
  };

  private aaSequenceInputRef = React.createRef<FormInput>();
  private fileSearchDropdownRef = React.createRef<FileSearchDropdown>();

  getInputElement(): HTMLInputElement | HTMLTextAreaElement | null | undefined {
    return this.aaSequenceInputRef.current?.inputRef.current;
  }

  override componentDidMount(): void {
    // Prevent <Esc> from closing the modal when the search results list is escaped.
    // This handler has to be added using JQuery because the handler we want to stop
    // is on the bootstrap modal.
    const input = this.getInputElement();
    if (input != null) {
      $(input).on('keyup', (e) => {
        if (keyboard.isEscapeKey(e) && this.state.query) {
          e.stopPropagation();
          this.setState({query: ''});
        }
      });
    }
  }

  override componentWillUnmount(): void {
    const input = this.getInputElement();
    if (input != null) {
      $(input).off();
    }
  }

  _handleSelectAttachment = (
    id: string,
    name: string,
    url: string,
    protein: AaSequenceWithPermissions
  ): void => {
    this.props.onChooseAaSequence(protein);
    this.setState({query: ''});
  };

  override render(): JSX.Element {
    return (
      <div>
        <FormInput
          type="text"
          placeholder="Search for an AA sequence on Benchling"
          value={this.state.query}
          onChange={(e: InputChangeEvent) => {
            this.setState({query: e.target.value});
          }}
          onKeyDown={(e: ElementKeyboardEvent) => {
            this.fileSearchDropdownRef.current?.queryKeydownHandler(e);
          }}
          ref={this.aaSequenceInputRef}
          focusOnMount={true}
          {...getTestProps({component: 'AaSequenceSearchInput', element: 'formInput'})}
        />
        {this.state.query !== '' && (
          <FileSearchDropdown
            ref={this.fileSearchDropdownRef}
            width="100%"
            query={this.state.query}
            fileType={ModelTypeConstants.PROTEIN}
            selectAttachment={this._handleSelectAttachment}
          />
        )}
      </div>
    );
  }
}

export default AaSequenceSearchInput;
